import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { AnimatePresence, motion, type PanInfo, useMotionValue, useReducedMotion, useTransform } from 'motion/react'
import * as React from 'react'

import { useIsMobile } from '../lib/useIsMobile'
import { cn } from '../lib/utils'

/**
 * A slide-out for supporting content read beside the page: a right-hand panel at md+ and a
 * bottom sheet below. Use `ResponsiveDialog` for tasks and decisions instead.
 */

type Presentation = 'auto' | 'side' | 'sheet'

export interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  /** Accessible name; rendered as the sr-only Radix title. */
  'aria-label': string
  /** Force a presentation regardless of viewport. Default `'auto'` (side ≥ md, sheet below). */
  presentation?: Presentation
  /** Pinned above the body, e.g. `DrawerHeader`. On the sheet it shares the drag zone with the handle. */
  header?: React.ReactNode
  /** `padded` (default) puts children in a padded scroller; `raw` hands them the whole panel. */
  layout?: 'padded' | 'raw'
  /** Extra classes on the panel / sheet. */
  className?: string
  /** Forwarded to Radix Content; `(e) => e.preventDefault()` keeps focus off the first control. */
  onOpenAutoFocus?: (event: Event) => void
  /** Side panel width: `default` (`max-w-xl`) or `peek` (`max-w-[30rem]`), which leaves more page beside it. */
  size?: 'default' | 'peek'
  /** Side presentation only: `blur` (default) or `tint`, which drops the blur so the page beside stays readable. */
  scrim?: 'blur' | 'tint'
  /** Sheet presentation only: `full` (default) height, or `fit` content up to 88dvh with the page showing above. */
  sheetHeight?: 'full' | 'fit'
  /** `card` (default) follows the theme; `deep` is brand-green-950 in both themes, with the sheet's handle floating over the content. */
  surface?: 'card' | 'deep'
}

interface DrawerContextValue {
  isSheet: boolean
  close: () => void
}

const DrawerContext = React.createContext<DrawerContextValue | null>(null)

function useDrawerContext(): DrawerContextValue {
  const ctx = React.useContext(DrawerContext)
  if (!ctx) throw new Error('DrawerHeader must be rendered inside a Drawer')
  return ctx
}

const SPRING = { type: 'spring' as const, stiffness: 380, damping: 34 }

export function Drawer({
  open,
  onOpenChange,
  children,
  'aria-label': ariaLabel,
  presentation = 'auto',
  header,
  layout = 'padded',
  className,
  onOpenAutoFocus,
  size = 'default',
  scrim = 'blur',
  sheetHeight = 'full',
  surface = 'card'
}: DrawerProps) {
  const autoMobile = useIsMobile()
  const isSheet = presentation === 'sheet' || (presentation === 'auto' && autoMobile)
  const reduced = !!useReducedMotion()
  const close = React.useCallback(() => onOpenChange(false), [onOpenChange])
  // No Radix Trigger, so Radix can't return focus on close; remember the opener ourselves.
  const returnFocusTo = React.useRef<HTMLElement | null>(null)
  const ctx = React.useMemo(() => ({ isSheet, close }), [isSheet, close])

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <motion.div
              aria-hidden
              className={cn('fixed inset-0 z-50 bg-foreground/10', (isSheet || scrim === 'blur') && 'backdrop-blur-[2px]')}
              initial={reduced ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduced ? 0 : 0.3, ease: 'easeOut' }}
            />
            <DialogPrimitive.Content
              forceMount
              aria-describedby={undefined}
              onOpenAutoFocus={(e) => {
                returnFocusTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
                onOpenAutoFocus?.(e)
              }}
              onCloseAutoFocus={(e) => {
                e.preventDefault()
                returnFocusTo.current?.focus()
                returnFocusTo.current = null
              }}
              // Backdrop clicks are handled on this layer below; stop Radix double-firing the close.
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
              className="fixed inset-0 z-50 outline-none"
              onClick={(e) => {
                if (e.target === e.currentTarget) close()
              }}>
              <DialogPrimitive.Title className="sr-only">{ariaLabel}</DialogPrimitive.Title>
              <DrawerContext.Provider value={ctx}>
                {isSheet ? (
                  <SheetSurface
                    reduced={reduced}
                    onClose={close}
                    header={header}
                    layout={layout}
                    height={sheetHeight}
                    surface={surface}
                    className={className}>
                    {children}
                  </SheetSurface>
                ) : (
                  <SideSurface reduced={reduced} header={header} layout={layout} size={size} surface={surface} className={className}>
                    {children}
                  </SideSurface>
                )}
              </DrawerContext.Provider>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  )
}

// `raw` skips the scroller so the caller can pin its own regions around one that scrolls.
function SurfaceBody({ layout, padding, children }: { layout: 'padded' | 'raw'; padding: string; children: React.ReactNode }) {
  if (layout === 'raw') return <>{children}</>
  return <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain', padding)}>{children}</div>
}

/* ─── Mobile: bottom sheet with swipe-to-dismiss ───────────────────── */

interface SheetSurfaceProps {
  reduced: boolean
  onClose: () => void
  header: React.ReactNode
  layout: 'padded' | 'raw'
  height: 'full' | 'fit'
  surface: 'card' | 'deep'
  className?: string
  children: React.ReactNode
}

function SheetSurface({ reduced, onClose, header, layout, height, surface, className, children }: SheetSurfaceProps) {
  const y = useMotionValue(0)
  const opacity = useTransform(y, [0, 300], [1, 0.2])

  const handleDragEnd = React.useCallback(
    (_: unknown, info: PanInfo) => {
      if (info.offset.y > 100 || info.velocity.y > 500) onClose()
    },
    [onClose]
  )

  return (
    <motion.div
      className={cn(
        'pointer-events-auto absolute flex flex-col overflow-hidden rounded-t-2xl',
        surface === 'deep' ? 'bg-brand-green-950' : 'bg-card',
        height === 'fit' ? 'inset-x-0 bottom-0 max-h-[88dvh] shadow-sheet' : 'inset-0',
        className
      )}
      style={{ y, opacity }}
      initial={reduced ? false : { y: '100%' }}
      animate={{ y: 0 }}
      // Exit animates only `y`: `opacity` is derived from it, and an exit target on a derived value
      // never settles, so the sheet and its overlay would never unmount and keep blocking the page.
      exit={reduced ? { y: 0 } : { y: '100%' }}
      transition={reduced ? { duration: 0 } : SPRING}>
      <motion.div
        className={cn('cursor-grab touch-none', surface === 'deep' ? 'absolute inset-x-0 top-0 z-[4]' : 'flex-shrink-0')}
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.6 }}
        onDrag={(_, info) => {
          y.set(Math.max(0, info.offset.y))
        }}
        onDragEnd={handleDragEnd}>
        <div className="flex justify-center pt-3 pb-1">
          <div className={cn('h-1 w-9 rounded-full', surface === 'deep' ? 'bg-white/45' : 'bg-foreground/15')} />
        </div>
        {header}
      </motion.div>
      {header && <div className="h-px flex-shrink-0 bg-border/60" />}
      <SurfaceBody layout={layout} padding="px-6 py-6">
        {children}
      </SurfaceBody>
    </motion.div>
  )
}

/* ─── Desktop: side panel from the right ───────────────────────────── */

interface SideSurfaceProps {
  reduced: boolean
  header: React.ReactNode
  layout: 'padded' | 'raw'
  size: 'default' | 'peek'
  surface: 'card' | 'deep'
  className?: string
  children: React.ReactNode
}

function SideSurface({ reduced, header, layout, size, surface, className, children }: SideSurfaceProps) {
  return (
    <motion.div
      className={cn(
        'pointer-events-auto absolute top-0 right-0 bottom-0 flex w-full flex-col overflow-hidden rounded-l-2xl border-l shadow-2xl',
        surface === 'deep' ? 'border-white/[0.06] bg-brand-green-950' : 'border-border/60 bg-card',
        size === 'peek' ? 'max-w-[30rem]' : 'max-w-xl',
        className
      )}
      initial={reduced ? false : { x: '100%' }}
      animate={{ x: 0 }}
      exit={reduced ? { opacity: 0 } : { x: '100%' }}
      transition={reduced ? { duration: 0 } : SPRING}>
      {header && (
        <>
          <div className="flex-shrink-0">{header}</div>
          <div className="h-px flex-shrink-0 bg-border/60" />
        </>
      )}
      <SurfaceBody layout={layout} padding="px-8 py-8">
        {children}
      </SurfaceBody>
    </motion.div>
  )
}

/* ─── Standard header — eyebrow + close, with an optional scroll-in title ───── */

export interface DrawerHeaderProps {
  /** Small uppercase context line, e.g. "Learn more" or the dinner's day. */
  eyebrow: React.ReactNode
  /** ~16px glyph before the eyebrow; inherits the muted colour. */
  icon?: React.ReactNode
  /** Crossfades in over the eyebrow while `showTitle` — typically once the body's own title scrolls away. */
  title?: React.ReactNode
  showTitle?: boolean
  /** Draws the bottom hairline; pass true once the body has scrolled. */
  divided?: boolean
}

function DrawerHeader({ eyebrow, icon, title, showTitle = false, divided = false }: DrawerHeaderProps) {
  const { isSheet, close } = useDrawerContext()
  const titled = showTitle && title != null
  const slot = 'absolute inset-0 flex items-center gap-2 truncate transition-[opacity,transform] duration-200 motion-reduce:transition-none'
  return (
    <div
      className={cn(
        'flex shrink-0 items-center gap-2 border-b py-2 pr-2 pl-5 transition-colors',
        isSheet ? 'min-h-[52px] pt-0' : 'min-h-[60px]',
        divided ? 'border-border' : 'border-transparent'
      )}>
      <div className="relative h-6 min-w-0 flex-1">
        <span
          className={cn(
            slot,
            'text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground',
            titled && '-translate-y-1.5 opacity-0'
          )}>
          {icon}
          {eyebrow}
        </span>
        {title != null && (
          <span
            aria-hidden={!titled}
            className={cn(
              slot,
              'block font-brand text-base font-medium text-foreground',
              titled ? 'opacity-100' : 'translate-y-1.5 opacity-0'
            )}>
            {title}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={close}
        className={cn(
          'flex size-11 shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent',
          'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300'
        )}>
        <X className="size-5" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  )
}
DrawerHeader.displayName = 'DrawerHeader'

export { DrawerHeader }
